import { Component, OnInit, OnChanges, ViewChild } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, interval, filter } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

import { User, AuthService, menuList, getCurrentMenu, Menu, isMobile, CustomIconService, Augmentations } from './core';
import { SnackbarService, ConfirmComponent, ModalConfirmComponent } from './shared';
import { CurrentFiltersService } from './shared/services/currentFilters/currentFilters.service';
import { environment } from '../environments/environment';
import { SUPERMERCADOS_CODE, PROMART_CODE } from './core/configs/company.config';
import { enabledStores } from './core/configs/dashboard.config';

import packageJson from '../../package.json';
import { SessionIdleService } from './shared';

declare let gtag: Function;

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnChanges {

  @ViewChild('sidenav') sidenav!: MatSidenav;

  user$!: Observable<User>;
  isLoggedIn$!: Observable<boolean>;

  reason = '';
  currentModule = '';
  previousModule = '';
  currentPage = '';
  previousPage = '';

  currentMenu!: Menu;
  currentTitle = '';
  currentVersion: string | null = null;

  isMobile = isMobile();
  sideNavState = false;
  menuList: any[] = [];
  showPage = false;

  version = '';
  envDevQA = false;

  numberOfVersionsFound = 0;

  constructor(
    public authService: AuthService,
    public snackbarService: SnackbarService,
    private router: Router,
    private currentFilterService: CurrentFiltersService,
    private swUpdate: SwUpdate,
    public dialog: MatDialog,
    private customIconService: CustomIconService,
    private sessionIdleService: SessionIdleService
  ) {

    this.version = packageJson.version;

    Augmentations.init();
    this.customIconService.init();

    this.envDevQA = window.location.hostname.includes('-');

    this.isLoggedIn$ = this.authService.isLoggedIn;

    this.isLoggedIn$.subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.menuList = JSON.parse(JSON.stringify(menuList));
      }
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {

        gtag('config', 'UA-156176128-1', {
          page_path: event.urlAfterRedirects
        });

        this.sideNavState = false;

        const urls = event.urlAfterRedirects.substring(1).split('/');

        this.currentModule = urls[0];
        this.currentPage = urls.length > 1 ? urls[1] : '';

        if (this.currentModule !== this.previousModule || this.currentPage !== this.previousPage) {
          this.snackbarService.hide();
        }

        this.previousModule = this.currentModule;
        this.previousPage = this.currentPage;

        this.currentMenu = getCurrentMenu(true);

        let currentTitle = this.currentMenu ? this.currentMenu.name : '';
        let currentVersion = null;

        if (!currentTitle && this.currentPage === '') {

          const url = document.location.pathname.substring(1).split('/');

          this.authService.user.subscribe((user: User) => {

            const exist = user?.visibleModules?.find(
              module => module.moduleIdentifier === url[0]
            );

            if (exist) {
              currentTitle = exist.moduleDescription;
              this.currentMenu = { module: 'currentTitle' } as Menu;
            } else {
              currentTitle = '<img src="../../../../assets/logo_dad_horizontal.png" alt="logoDaD" height="40" />';
              currentVersion = this.version;
            }

          });

        } else if (!currentTitle) {
          this.currentMenu = { module: 'currentTitle' } as Menu;
        }

        this.currentTitle = currentTitle;
        this.currentVersion = currentVersion;

        this.validateModules();

      });

  }

  ngOnChanges() { }

  ngOnInit() {
    interval(180000).subscribe(() => {
      this.swUpdate.checkForUpdate();
    });

    if (this.swUpdate.isEnabled) {

      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(() => {

          this.numberOfVersionsFound++;

          if (this.numberOfVersionsFound === 1) {

            const openModal = this.dialog.open(ConfirmComponent, {
              data: {
                message: 'Hay una nueva versión del sistema.<br>La página se actualizará.',
                no: ''
              },
              disableClose: true
            });

            openModal.afterClosed().subscribe(result => {

              if (result) {
                this.numberOfVersionsFound = 0;
                window.location.reload();
              }

            });
          }
        });

    }

    this.validateSession();
    this.showSession();

  }

  showSession() {

    this.currentFilterService.currentSessionFilters$
      .subscribe(value => {
        if (value) {
          this.sessionIdleService.resetTimer();
        }
      });

  }

  validateSession() {
    this.sessionIdleService.startWatching();
    this.sessionIdleService.onTimeout$
      .subscribe(() => {

        if (this.authService.getUser()) {
          const openGrabar = this.dialog.open(ModalConfirmComponent, {
            data: 'sessionExpired',
            disableClose: true
          });

          openGrabar.afterClosed()
            .subscribe(() => {

              this.authService.logout();
              window.location.reload();

            });
        }
      });

  }

  validateModules() {

    const isLogged = localStorage.getItem(environment.sessionAuth);

    const userSession = isLogged
      ? JSON.parse(localStorage.getItem(environment.sessionAuth)!).user
      : null;

    this.authService.user.subscribe((user: User) => {

      if (!user) return;

      if (!user.visibleModules || user.visibleModules.length < 1) {

        this.authService.logout();

        this.router.navigate(['/iniciar-sesion']);

        this.snackbarService.show(
          'El usuario no tiene roles asignados, comuníquese con el administrador del sistema.',
          'warning'
        );

        return;
      }

      const pageFound = user.visibleModules
        .find(module => module.moduleIdentifier === this.currentModule);

      if (this.currentModule === '' || pageFound) {

        this.showPage = true;

        this.menuList.map(menu => {

          if (menu.module === 'configuration') {

            menu.subMenuList = menu.subMenuList
              .filter(menu => this.envDevQA
                ? menu.name !== "Entidades"
                : menu.name !== "Entidades con capacidad");

            menu.subMenuList = menu.subMenuList
              .filter(menu => menu.module !== 'entityCapabilitiesHidden'
                && menu.module !== 'deliveryWindowsSettingsHidden'
                && menu.module !== 'calculationRangeHidden');
          }

          if (menu.module === 'picking' && isLogged) {

            menu.subMenuList = menu.subMenuList
              .filter(menu =>
                userSession.entity.company.code !== SUPERMERCADOS_CODE
                  ? menu.module !== 'pickingAudit'
                  : true);

          }

          if (menu.module === 'report' && isLogged) {

            menu.subMenuList = menu.subMenuList
              .filter(menu =>
                userSession.entity.company.code !== SUPERMERCADOS_CODE
                  ? menu.module !== 'auditReport'
                  : true);

          }

          if (menu.module === 'dashboard' && isLogged) {

            menu.subMenuList = menu.subMenuList
              .filter(menu => {

                if (userSession.entity.company.code === PROMART_CODE) {
                  return !enabledStores.SPSA.includes(menu.module);
                }

                if (userSession.entity.company.code === SUPERMERCADOS_CODE) {
                  return !enabledStores.PROMART.includes(menu.module);
                }

                return !enabledStores.SPSA.includes(menu.module)
                  && !enabledStores.PROMART.includes(menu.module);

              });

          }

          return menu;

        });

        for (let i = this.menuList.length - 1; i >= 0; i--) {

          const moduleFound = user.visibleModules.find(
            module => module.moduleIdentifier === this.menuList[i].link.substring(1)
          );

          if (moduleFound) {

            this.menuList[i].name = moduleFound.moduleName;
            this.menuList[i].description = moduleFound.moduleDescription;
            this.menuList[i].active = moduleFound.active;

          } else {

            this.menuList.splice(i, 1);

          }

        }

      }

    });

  }

  onSinenavToggle() {
    this.sideNavState = !this.sideNavState;
  }

  close(reason: string) {
    this.reason = reason;
    this.sidenav.close();
  }

}